import {
  DropdownButton,
  DropdownButtonType
} from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';

import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { getExplorableItemDefinition} from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { MixedContentItemDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsTypes';
import { ExplorableItemType } from '../../models/ContentType';
import { SpaceType } from '../../state/SpaceInfo';
import { ConnectedFilterByTypePanel } from './ConnectedFilterbyTypePanel';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';

export interface FilterByTypeDropdownProps {
  readonly filterSelected: (selectedContentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly spaceType: SpaceType;
  readonly showWorkbooksAsViews?: boolean;
  readonly toggleShowWorkbooksAsViews?: (showWorkbooksAsViews: boolean) => void;
  readonly contentItemDefinitions: MixedContentItemDefinition[];
}

export class FilterByTypeDropdown extends React.PureComponent<FilterByTypeDropdownProps> {
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  public componentDidMount() {
    // if navigating between pages in the same space (e.g. one project to another) need to clear the previous counts,
    // otherwise menu will show previous counts from another menu until contentCountsRequest completes
    this.props.clearContentCounts(this.props.spaceType);
  }

  public close = (): void => {
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.toggle(false);
    }
  };

  public render(): JSX.Element {
    return (
      <DropdownButton
        aria-labelledby={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel}
        aria-controls={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-popup`}
        aria-haspopup={'dialog'}
        label={''}
        ref={this.dropdownButtonRef}
        closeOnPopupBlur={true}
        type={DropdownButtonType.Line}
        testId={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-Button`}
        popupContent={
          <div id={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-popup`} role={'dialog'}>
            <ConnectedFilterByTypePanel
              defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
              toggleShowWorkbooksAsViews={this.props.toggleShowWorkbooksAsViews}
              filterSelected={this.props.filterSelected}
              closePanel={this.close}
              contentItemDefinitions={this.props.contentItemDefinitions}
            />
          </div>
        }
        style={{ minWidth: 130 }}
      >
        {this.renderButtonContentUpdated()}
      </DropdownButton>
    );
  }

  private renderButtonContentUpdated = (): JSX.Element => {
    return <span>{this.getButtonTextUpdated()}</span>;
  };

  private getButtonTextUpdated = (): string => {
    const selectedCount = this.props.contentFilter.length;
    const maxSelectedCount = this.props.defaultContentTypeFilterValues.length;

    if(selectedCount < 1) {
      return; // should never reach this state
    }

    if (selectedCount === maxSelectedCount) {
      return Messages.all();
    } else if(selectedCount === 1) {
      return this.getButtonTextForSingleContentType();
    } else {
      return Messages.filterByTypeDropdown_someSelected({
        FIRST_CONTENT_TYPE: getExplorableItemDefinition(this.props.contentFilter[0]).pluralName(),
        ADDITIONAL_COUNT_LOC: L10n.localizedCount(selectedCount - 1)
      });
    }
  };

  private getButtonTextForSingleContentType() {
    if (this.props.contentFilter[0] === 'dataconnection') {
      return getExplorableItemDefinition('datasource').pluralName();
    }

    // TFSID 977220: Check if itemDef exists in case the content type in the store does not yet match,
    // only necessary to check when contentFilter.length === 1
    const itemDef = getExplorableItemDefinition(this.props.contentFilter[0]);
    if (!_.isUndefined(itemDef)) {
      return itemDef.pluralName();
    } else {
      return Messages.all();
    }
  }
}

/**
 * Modifies the ContentType array provided, swapping workbook for view, if showWorkbooksAsViews is true;
 * Otherwise, it swaps view for workbook. If neither is present in the array, it is not modified at all.
 */
export function swapWorkbookAndViewContentTypes(selectedContentTypes: ExplorableItemType[], showWorkbooksAsViews: boolean) {
  const swapFrom = showWorkbooksAsViews ? 'workbook' : 'view';
  const swapWith = showWorkbooksAsViews ? 'view' : 'workbook';
  const indexOfSwap = selectedContentTypes.indexOf(swapFrom);
  if (indexOfSwap > -1) {
    selectedContentTypes[indexOfSwap] = swapWith;
  }
}

/**
 * Modifies the ContentType array provided, adding or removing the specified contentTypeToToggle.
 */
export function toggleSelectedContentType(selectedContentTypes: ExplorableItemType[], contentTypeToToggle: ExploreContentApi_ContentType) {
    const selectedIndex = selectedContentTypes.indexOf(contentTypeToToggle);

    if (selectedIndex > -1) {
      selectedContentTypes.splice(selectedIndex, 1);
    } else {
      selectedContentTypes.push(contentTypeToToggle);
    }
}
